import { ArrowLeft, ExternalLink, File, Github } from 'lucide-react';
import { formatDurationMs, getActionVisual } from '../actionVisuals';
import ActionDetailTimeline from './ActionDetailTimeline';
import ActionDetailExplainer from './ActionDetailExplainer';
import { useAgentActionDetail } from '../hooks/useAgentActions';
import type { ActionColor } from '../types';

interface ActionDetailProps {
  actionId: string;
  onBack: () => void;
}

function detailColor(type: string, result: Record<string, unknown>): ActionColor {
  if (result.status === 'no_fix') return 'gray';
  if (result.status === 'error') return 'danger';
  if (result.status === 'resolved') return 'success';
  if (type === 'fix') return 'success';
  if (type === 'deploy') return 'info';
  if (type === 'sync') return 'purple';
  if (type === 'template_authored') return 'coral';
  return 'info';
}

function resultLabel(result: Record<string, unknown>) {
  if (result.status === 'no_fix') return { text: 'No change', className: 'text-gray-500 dark:text-gray-400' };
  if (result.status === 'fix_pushed' || result.fixed === true) return { text: 'Fixed', className: 'text-green-600 dark:text-green-400' };
  if (result.status === 'error') return { text: 'Failed', className: 'text-red-600 dark:text-red-400' };
  if (result.status === 'resolved') return { text: 'Resolved', className: 'text-green-600 dark:text-green-400' };
  if (result.status === 'partial_failure') return { text: 'Partial failure', className: 'text-amber-600 dark:text-amber-400' };
  if (typeof result.status === 'string') return { text: result.status, className: 'text-gray-900 dark:text-gray-100' };
  return { text: '—', className: 'text-gray-900 dark:text-gray-100' };
}

function deployLabel(deployStatus: unknown) {
  if (deployStatus === 'dispatch_failed' || deployStatus === 'not_dispatched') {
    return { text: 'Not dispatched (no workflow in repo)', className: 'text-amber-600 dark:text-amber-400' };
  }
  if (typeof deployStatus === 'string' && deployStatus) {
    return { text: deployStatus, className: 'text-gray-900 dark:text-gray-100' };
  }
  return { text: '—', className: 'text-gray-900 dark:text-gray-100' };
}

export default function ActionDetail({ actionId, onBack }: ActionDetailProps) {
  const detail = useAgentActionDetail(actionId);
  const d = detail.data;

  if (detail.isLoading) {
    return <div className="text-sm text-gray-500 py-8">Loading action detail…</div>;
  }

  if (detail.isError || !d) {
    return (
      <div className="space-y-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center bg-transparent border border-gray-200 dark:border-gray-700 px-2.5 py-1.5 rounded-md text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
        </button>
        <div className="text-sm text-red-600">Could not load action detail.</div>
      </div>
    );
  }

  const result = d.result || {};
  const githubUrl = typeof result.github_url === 'string' ? result.github_url : null;
  const filesChanged = Array.isArray(result.files_changed) ? result.files_changed as string[] : [];
  const color = detailColor(d.type, result);
  const visual = getActionVisual({
    type: d.type,
    color,
    icon: color === 'danger' ? 'alert-triangle' : d.type === 'fix' ? 'tool' : 'rocket',
    has_attention_required: color === 'danger',
    status: typeof result.status === 'string' ? result.status : null,
  });
  const { Icon } = visual;
  const resultInfo = resultLabel(result);
  const deployInfo = deployLabel(result.deploy_status);
  const duration = formatDurationMs(d.metrics.duration_ms);
  const triggerLine = [
    d.trigger?.by_app || d.app_name,
    d.app_id ? `(${d.app_id})` : null,
    duration ? `· ${duration}` : null,
  ].filter(Boolean).join(' ');

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center bg-transparent border border-gray-200 dark:border-gray-700 px-2.5 py-1.5 rounded-md text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
        </button>
        <p className="text-xs text-gray-500 font-mono m-0">{d.id}</p>
      </div>

      <div className={`flex items-center gap-3.5 mb-6 rounded-lg border p-4 ${visual.rowTintClass} ${visual.rowBorderClass}`}>
        <div
          className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${visual.circleClass}`}
          style={visual.circleStyle}
        >
          <Icon className={`w-[22px] h-[22px] ${visual.iconClass}`} style={visual.iconStyle} />
        </div>
        <div>
          <p className="text-base font-semibold text-gray-900 dark:text-gray-100 m-0">{d.title}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 m-0">Triggered by {triggerLine}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        {[
          { label: 'Result', value: resultInfo.text, valueClass: resultInfo.className },
          {
            label: 'Files changed',
            value: d.metrics.files_changed_count === 1 ? '1 file' : `${d.metrics.files_changed_count} files`,
            valueClass: 'text-gray-900 dark:text-gray-100',
          },
          { label: 'Deploy', value: deployInfo.text, valueClass: deployInfo.className },
        ].map((m) => (
          <div key={m.label} className="bg-gray-50 dark:bg-gray-800 rounded-md p-3 border border-gray-200/60 dark:border-gray-700">
            <p className="text-[11px] text-gray-500 uppercase tracking-wide m-0 mb-1">{m.label}</p>
            <p className={`text-sm font-semibold m-0 ${m.valueClass}`}>{m.value}</p>
          </div>
        ))}
      </div>

      {typeof result.resolution === 'string' && result.resolution && (
        <div className="mb-6 rounded-md border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/40 px-3 py-2 text-sm text-green-700 dark:text-green-300">
          {result.resolution}
        </div>
      )}

      <p className="text-sm font-medium text-gray-500 m-0 mb-3">What happened</p>
      <div className="mb-6">
        <ActionDetailTimeline events={d.timeline} />
      </div>

      {filesChanged.length > 0 && (
        <>
          <p className="text-sm font-medium text-gray-500 m-0 mb-2">Files changed</p>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-md p-3 mb-6 border border-gray-200/60 dark:border-gray-700">
            {filesChanged.map((f) => (
              <p key={f} className="text-sm font-mono text-gray-600 dark:text-gray-400 m-0 flex items-center gap-1.5">
                <File className="w-3.5 h-3.5 flex-shrink-0" />
                {f}
              </p>
            ))}
          </div>
        </>
      )}

      <div className="flex flex-wrap gap-2 items-start">
        {githubUrl && (
          <a
            href={githubUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <Github className="w-3.5 h-3.5" />
            View commit on GitHub
            <ExternalLink className="w-3 h-3 opacity-60" />
          </a>
        )}
        <ActionDetailExplainer detail={d} />
      </div>
    </div>
  );
}
